import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { FOUR_GODS_CONFIG, FOUR_GODS_SHARED_IMAGES } from '../../constants/fourGodsConfig';
import useVersionCheck from '../../hooks/useVersionCheck';

/**
 * FourGodsPage - 四神獣キャラクターランディングページ
 *
 * URLの :godId（genbu / byakko / suzaku / seiryu）に応じて
 * 背景・キャラクター画像・テキスト・キーワードを出し分ける。
 *
 * レイアウト（画面上から順）:
 *   1. キャラクター画像 ＋ タイトルロゴ（縦並び・中央揃え）
 *   2. 説明テキスト
 *   3. キーワード入力欄 ＋ 送信ボタン
 *   4. エラーメッセージ（任意表示）
 *   5. リニューアルロゴ ＋ ©RURAL ©KFP（画面下部）
 */
const FourGodsPage = () => {
  const { godId } = useParams();
  const config = FOUR_GODS_CONFIG[godId];

  useVersionCheck();

  const [keyword, setKeyword] = useState('');
  const [showError, setShowError] = useState(false);

  if (!config) {
    window.location.replace('https://diary.rural.ne.jp/');
    return null;
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmed = keyword.trim();

    if (trimmed === config.keyword) {
      window.location.href = config.pushPageUrl;
    } else {
      setShowError(true);
    }
  };

  const handleChange = (e) => {
    setKeyword(e.target.value);
    if (showError) setShowError(false);
  };

  return (
    <div
      className="relative flex flex-col items-center w-full h-[100dvh] overflow-hidden"
    >
      {/* 背景画像（薄く表示するため opacity を下げる） */}
      <div
        className="fixed inset-0 -z-10 bg-cover bg-center bg-no-repeat opacity-20"
        style={{ backgroundImage: `url(${config.backgroundImage})` }}
        aria-hidden="true"
      />

      <div className="flex flex-col items-center justify-between w-full max-w-md h-full px-6 py-3 overflow-hidden">
        <div className="flex flex-col items-center w-full min-h-0">
          {/* ① キャラクター画像 ＋ タイトルロゴ（縦並び・中央揃え） */}
          <div className="flex flex-col items-center justify-center gap-2 w-full min-h-0">
            <img
              src={FOUR_GODS_SHARED_IMAGES.title}
              alt="けもフレ2026 新宿イベント"
              className="w-full max-w-[400px] object-contain shrink-0"
            />
            <img
              src={config.characterImage}
              alt=""
              className="max-h-[30dvh] w-auto object-contain shrink"
            />
          </div>

          {/* ② 説明テキスト */}
          <p className="mt-3 text-center text-sm leading-relaxed whitespace-pre-line">
            {config.text}
          </p>

          {/* ③ キーワード入力欄 ＋ 送信ボタン */}
          <form onSubmit={handleSubmit} className="flex flex-col items-center w-full mt-3 gap-2">
            <input
              type="text"
              value={keyword}
              onChange={handleChange}
              className="w-full h-11 px-3 border border-gray-800 rounded-sm bg-white text-center focus:outline-none"
              placeholder="ひみつのことば"
            />
            <button
              type="submit"
              className="px-8 py-2 border border-gray-800 rounded-sm bg-white text-sm active:bg-gray-100"
            >
              送信
            </button>
          </form>

          {/* ④ エラーメッセージ */}
          {showError && (
            <p className="mt-2 text-center text-sm text-red-600">
              {config.errorMessage}
            </p>
          )}
        </div>

        {/* ⑤ リニューアルロゴ（画面下部） */}
        <div className="flex flex-col items-center gap-1 shrink-0">
          <img
            src={FOUR_GODS_SHARED_IMAGES.logo}
            alt="けものコレクション DIARY"
            className="max-h-[8dvh] w-auto object-contain"
          />
          <p className="text-center text-xs text-gray-600">©RURAL ©KFP</p>
        </div>
      </div>
    </div>
  );
};

export default FourGodsPage;
